import type { DocumentoResumenVisitaApi, Visita } from '../services/visitas.service';

/** Metadato normalizado del resumen firmado (listado / detalle). */
export interface VisitaResumenFirmadoAdjunto {
  ruta_almacenamiento: string;
  nombre_archivo: string;
  mime_type?: string | null;
  fecha_subida?: string | null;
  tamano_bytes?: number | null;
  url_descarga?: string | null;
}

function adjuntoDesdeObjetoApi(o: Record<string, unknown>): VisitaResumenFirmadoAdjunto | null {
  const rutaRaw = o['ruta_almacenamiento'] ?? o['rutaAlmacenamiento'] ?? o['ruta'];
  const ruta = rutaRaw != null ? String(rutaRaw).trim() : '';
  if (!ruta) return null;

  const nombreRaw =
    o['nombre_archivo'] ?? o['nombreArchivo'] ?? o['nombre'] ?? o['filename'];
  const nombre = nombreRaw != null ? String(nombreRaw).trim() : '';

  const mimeRaw = o['mime_type'] ?? o['mimeType'];
  const urlRaw = o['url_descarga'] ?? o['urlDescarga'];
  const fechaRaw = o['fecha_subida'] ?? o['fechaSubida'];
  const tamanoRaw = o['tamano_bytes'] ?? o['tamanoBytes'];

  return {
    ruta_almacenamiento: ruta,
    nombre_archivo: nombre || ruta.split('/').pop() || 'documento',
    mime_type: mimeRaw != null ? String(mimeRaw) : null,
    fecha_subida: fechaRaw != null ? String(fechaRaw) : null,
    tamano_bytes: tamanoRaw != null ? Number(tamanoRaw) : null,
    url_descarga: urlRaw != null ? String(urlRaw) : null,
  };
}

/**
 * Une `documento_resumen` del GET detalle y del ítem de listado (Ver Detalle / historial).
 */
export function resolverDocumentoResumenVisita(
  detalle: Visita,
  origen?: Visita | null
): DocumentoResumenVisitaApi | null | undefined {
  const regBase = detalle.registro_visita;
  const regOrigen = origen?.registro_visita;
  return (
    detalle.documento_resumen ??
    origen?.documento_resumen ??
    regBase?.documento_resumen ??
    regOrigen?.documento_resumen ??
    null
  );
}

/**
 * Extrae metadatos del resumen firmado desde un ítem de visita.
 * Fuentes: `documento_resumen` en raíz o `registro_visita.documento_resumen` del listado.
 */
export function extraerAdjuntoResumenFirmadoDesdeVisita(
  visita: Visita | null | undefined
): VisitaResumenFirmadoAdjunto | null {
  if (!visita) return null;

  const docRaiz = (visita as Visita & { documento_resumen?: unknown }).documento_resumen;
  if (docRaiz != null && typeof docRaiz === 'object') {
    const parsed = adjuntoDesdeObjetoApi(docRaiz as Record<string, unknown>);
    if (parsed) return parsed;
  }

  const registro = visita.registro_visita as
    | (typeof visita.registro_visita & { documento_resumen?: unknown })
    | null
    | undefined;
  const docRegistro = registro?.documento_resumen;
  if (docRegistro != null && typeof docRegistro === 'object') {
    const parsed = adjuntoDesdeObjetoApi(docRegistro as Record<string, unknown>);
    if (parsed) return parsed;
  }

  return null;
}

/** Indica si la visita ya tiene un resumen firmado en servidor. */
export function visitaTieneDocumentoResumenFirmado(
  visita: Visita | null | undefined
): boolean {
  return extraerAdjuntoResumenFirmadoDesdeVisita(visita) != null;
}

/** Estado «Finalizada» (criterio explícito para subir resumen firmado). Acepta «Completada» por compat. */
export function esEstadoVisitaFinalizada(estado: string | null | undefined): boolean {
  const e = (estado ?? '').toLowerCase();
  return e === 'finalizada' || e === 'completada';
}
